// filepath: admin-dashboard/src/utils/envConfig.ts
/**
 * Environment configuration utility for React applications
 * This utility provides a clean interface for accessing environment variables
 * in browser environments where .env files are handled by the build process
 */

export interface EnvironmentConfig {
  apiBaseUrl: string;
  defaultLocale: string;
  nodeEnv: string;
  isProduction: boolean;
  isDevelopment: boolean;
}

/**
 * Gets the current environment configuration
 * In React applications, environment variables are injected at build time
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  const nodeEnv = process.env.NODE_ENV || 'development';

  return {
    apiBaseUrl: process.env.REACT_APP_API_BASE_URL || '',
    defaultLocale: process.env.REACT_APP_DEFAULT_LOCALE || 'en',
    nodeEnv,
    isProduction: nodeEnv === 'production',
    isDevelopment: nodeEnv === 'development',
  };
}

/**
 * Utility function to get a specific REACT_APP environment variable
 * with type safety and default value support
 */
export function getEnvVar(key: string, defaultValue: string = ''): string {
  if (!key.startsWith('REACT_APP_')) {
    throw new Error(
      `Environment variable key must start with 'REACT_APP_': ${key}`
    );
  }
  return process.env[key] || defaultValue;
}

/**
 * Convenience function that returns the environment configuration
 * In React apps, environment variables are available immediately
 */
export function initializeEnvironment(): EnvironmentConfig {
  return getEnvironmentConfig();
}
